import { useState, useCallback, useRef, useEffect } from "react";
import {
  Box, Sheet, Card, Typography, Input, Button, IconButton, Chip, Alert,
  CircularProgress, Divider, Select, Option, Table, Tabs, TabList, Tab, TabPanel, Tooltip, Checkbox,
} from "@mui/joy";
import { motion } from "framer-motion";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { adminApi } from "../adminApi";

const LEVEL_COLOR = { error: "danger", warn: "warning", info: "primary", debug: "neutral" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("ru-RU") : "—");
const short = (s, n = 8) => (s ? String(s).slice(0, n) : "—");

// Ссылка для входа под клиентом + флаги «умной ссылки» (?fit_image / ?converter),
// которые включают для этого клиента сжатие фото и конвертер HEIC.
function buildClientLink(base, compress, convert) {
  if (!base) return base;
  const flags = [];
  if (compress) flags.push("fit_image");
  if (convert) flags.push("converter");
  if (!flags.length) return base;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}${flags.join("&")}`;
}

// ── Плитка-счётчик ──
function StatTile({ label, value, color }) {
  return (
    <Sheet variant="soft" color={color} sx={{ flex: 1, minWidth: 120, borderRadius: "12px", p: 1.5 }}>
      <Typography level="h2" sx={{ lineHeight: 1 }}>{value ?? 0}</Typography>
      <Typography level="body-xs">{label}</Typography>
    </Sheet>
  );
}

// ── Пагинатор блока ──
function Pager({ block, onPage }) {
  if (!block || (block.pagination !== "cursor" && block.num_pages <= 1)) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
      <Button size="sm" variant="outlined" color="neutral" disabled={!block.has_prev}
              onClick={() => onPage(block.page - 1)}>← назад</Button>
      <Typography level="body-xs" color="neutral">стр. {block.page}{block.num_pages ? ` из ${block.num_pages}` : ""}</Typography>
      <Button size="sm" variant="outlined" color="neutral" disabled={!block.has_next}
              onClick={() => onPage(block.page + 1)}>вперёд →</Button>
    </Box>
  );
}

function LogRow({ log, params }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const request = useRef(null);
  useEffect(() => () => request.current?.abort(), []);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (detail) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true); setError("");
    try {
      const result = await adminApi.logsLookup(
        { ...params, section: "detail", log_id: log.id },
        { signal: controller.signal },
      );
      if (!controller.signal.aborted) setDetail(result.log);
    } catch (err) {
      if (!controller.signal.aborted) setError(err.message || "Не удалось получить подробности.");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  return (
    <>
      <tr>
        <td>
          <Typography level="body-xs">{fmtDate(log.client_ts || log.created_at)}</Typography>
          <Typography level="body-xs" color="neutral">{fmtDate(log.created_at)}</Typography>
        </td>
        <td><Chip size="sm" variant="soft" color={LEVEL_COLOR[log.level] || "neutral"}>{log.level}</Chip></td>
        <td>
          {log.source === "qc"
            ? <Chip size="sm" variant="soft" color="warning">QC</Chip>
            : <Typography level="body-xs">{log.source_display || log.source}</Typography>}
        </td>
        <td><Typography level="body-sm" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{log.event}</Typography></td>
        <td>
          <Typography level="body-sm" sx={{ overflowWrap: "anywhere" }}>{log.message}</Typography>
          {log.message_truncated && <Typography level="body-xs" color="neutral">Продолжение — в подробностях</Typography>}
          {log.url && <Typography level="body-xs" color="neutral" sx={{ wordBreak: "break-all" }}>{log.url}</Typography>}
          <Button size="sm" variant="plain" color="neutral" sx={{ mt: 0.5, minHeight: 0, py: 0.25 }}
                  loading={loading}
                  endDecorator={<KeyboardArrowDownRoundedIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: ".2s" }} />}
                  onClick={toggle}>
            Подробности
          </Button>
        </td>
      </tr>
      {open && (
        <tr><td colSpan={5}>
          {loading && <Typography level="body-sm">Загрузка…</Typography>}
          {error && <Alert color="danger">{error}</Alert>}
          {detail && <>
            {log.message_truncated && <Typography level="body-sm" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{detail.message}{detail.message_truncated ? "…" : ""}</Typography>}
            {detail.payload_omitted && <Alert color="warning" sx={{ my: 1 }}>{detail.payload_note}</Alert>}
            {!detail.payload_omitted && <Box component="pre" sx={{
              m: 0, p: 1.5, borderRadius: "8px", bgcolor: "#1e1e1e", color: "#d4d4d4",
              fontSize: 12, overflow: "auto", maxHeight: 320,
            }}>{JSON.stringify(detail.payload, null, 2)}</Box>}
          </>}
        </td></tr>
      )}
    </>
  );
}

export default function LogsPage() {
  const [codeInput, setCodeInput] = useState("");
  const [bookInput, setBookInput] = useState("");
  const [orderInput, setOrderInput] = useState("");
  const [level, setLevel] = useState("");
  const [source, setSource] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pages, setPages] = useState({ books_page: 1, orders_page: 1, processes_page: 1, logs_page: 1 });
  const [linkCompress, setLinkCompress] = useState(false);
  const [linkConvert, setLinkConvert] = useState(false);

  const [activeQuery, setActiveQuery] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");
  const [range, setRange] = useState("24h");
  const [scope, setScope] = useState("target");
  const [logCursors, setLogCursors] = useState([null]);
  const [exporting, setExporting] = useState(false);
  const [exportRange, setExportRange] = useState("24h");
  const [exportInfo, setExportInfo] = useState(null);
  const generation = useRef(0);
  const summaryRequest = useRef(null);
  const logRequest = useRef(null);
  const exportRequest = useRef(null);

  useEffect(() => () => {
    generation.current += 1;
    summaryRequest.current?.abort();
    logRequest.current?.abort();
    exportRequest.current?.abort();
  }, []);

  const activeParams = useCallback((overrides = {}) => {
    const params = { level, source, range, scope, ...pages, ...overrides };
    const book = (overrides.book ?? bookInput).trim();
    const order = (overrides.order ?? orderInput).trim();
    const code = (overrides.code ?? codeInput).trim();
    if (book) params.book = book;
    else if (order) params.order = order;
    else if (code) params.code = code;
    return params;
  }, [codeInput, bookInput, orderInput, level, source, range, scope, pages]);

  const loadLogs = useCallback(async (params, cursor = null, page = 1) => {
    logRequest.current?.abort();
    const controller = new AbortController();
    const version = generation.current;
    logRequest.current = controller;
    setLogsLoading(true);
    setLogsError("");
    try {
      const response = await adminApi.logsLookup(
        { ...params, section: "logs", before_id: cursor || undefined },
        { signal: controller.signal },
      );
      if (controller.signal.aborted || version !== generation.current) return;
      setData((previous) => previous ? {
        ...previous, logs: response.logs ? { ...response.logs, page } : null,
      } : previous);
    } catch (err) {
      if (!controller.signal.aborted && version === generation.current) {
        setLogsError(err.message || "Не удалось загрузить события. Повторите запрос.");
      }
    } finally {
      if (!controller.signal.aborted && version === generation.current) setLogsLoading(false);
    }
  }, []);

  const run = useCallback(async (params, { search = false } = {}) => {
    summaryRequest.current?.abort();
    const controller = new AbortController();
    summaryRequest.current = controller;
    if (search) {
      generation.current += 1;
      logRequest.current?.abort();
      exportRequest.current?.abort();
      setLogsLoading(false);
      setExporting(false);
      setData(null);
      setLogsError("");
      setExportInfo(null);
      setLogCursors([null]);
      setActiveQuery(null);
    }
    const version = generation.current;
    setLoading(true);
    setError("");
    try {
      const response = await adminApi.logsLookup(
        { ...params, section: "summary" }, { signal: controller.signal },
      );
      if (controller.signal.aborted || version !== generation.current) return;
      setData((previous) => search ? response : { ...previous, ...response });
      if (response.found && search) {
        setActiveQuery(params);
        // Link and summary are already visible; event errors have their own state.
        loadLogs(params);
      }
    } catch (err) {
      if (!controller.signal.aborted && version === generation.current) {
        setError(err.message || "Ошибка загрузки");
      }
    } finally {
      if (!controller.signal.aborted && version === generation.current) setLoading(false);
    }
  }, [loadLogs]);

  const onSearch = () => {
    const reset = { books_page: 1, orders_page: 1, processes_page: 1, logs_page: 1 };
    const params = activeParams(reset);
    if (!params.book && !params.order && !params.code) {
      setError("Введите номер заказа, UUID книги или код техподдержки.");
      return;
    }
    setPages(reset);
    run(params, { search: true });
  };

  const onFilter = (change) => {
    const next = { level, source, range, scope, ...change };
    setLevel(next.level); setSource(next.source);
    setRange(next.range); setScope(next.scope);
    setPages((previous) => ({ ...previous, logs_page: 1 }));
    setLogCursors([null]);
    exportRequest.current?.abort();
    setExporting(false);
    setExportInfo(null);
    if (activeQuery) {
      const params = { ...activeQuery, ...next };
      setActiveQuery(params);
      setData((previous) => ({ ...previous, logs: null }));
      loadLogs(params);
    }
  };

  const onPage = (key, number) => {
    if (!activeQuery) return;
    if (key === "logs_page") {
      let cursors = logCursors;
      if (number > (data?.logs?.page || 1)) {
        if (!data?.logs?.next_cursor) return;
        cursors = [...logCursors.slice(0, number - 1), data.logs.next_cursor];
        setLogCursors(cursors);
      }
      loadLogs(activeQuery, cursors[number - 1], number);
    } else {
      setPages((previous) => ({ ...previous, [key]: number }));
      const params = { ...activeQuery, ...pages, [key]: number };
      setActiveQuery(params);
      run(params);
    }
  };

  const onExport = async (continuePart = false) => {
    if (!activeQuery || exporting) return;
    exportRequest.current?.abort();
    const controller = new AbortController();
    exportRequest.current = controller;
    const version = generation.current;
    setExporting(true);
    setLogsError("");
    try {
      const params = {
        ...activeQuery, range: exportRange,
        before_id: continuePart ? exportInfo?.next_before_id : undefined,
        as_of: continuePart ? exportInfo?.as_of : undefined,
      };
      const response = await adminApi.logsExportBlob(params, { signal: controller.signal });
      if (controller.signal.aborted || version !== generation.current) return;
      const info = JSON.parse(await response.data.text());
      if (controller.signal.aborted || version !== generation.current) return;
      const disposition = response.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/);
      const fallback = `logs_${params.order || params.book || params.code}_${params.before_id || "latest"}.json`;
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url; anchor.download = match ? match[1] : fallback;
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExportInfo({
        count: info.count, next_before_id: info.next_before_id, as_of: info.as_of,
        omitted: info.oversized_payloads_omitted,
      });
    } catch (err) {
      if (!controller.signal.aborted && version === generation.current) {
        let message = err.message;
        // Axios returns an error body as Blob for a download request.
        if (err.data?.text) {
          try { const body = JSON.parse(await err.data.text()); message = body.detail || message; } catch { /* use fallback */ }
        }
        if (!controller.signal.aborted && version === generation.current) {
          setLogsError(message || "Не удалось скачать JSON.");
        }
      }
    } finally {
      if (!controller.signal.aborted && version === generation.current) setExporting(false);
    }
  };

  const copy = (text) => { try { navigator.clipboard.writeText(text); } catch (e) { /* ignore */ } };

  const counts = data?.counts || {};

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Typography level="h3">Логи клиента</Typography>

      {/* Поиск */}
      <Sheet variant="outlined" sx={{ p: 2, borderRadius: "14px" }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Box>
            <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>Код техподдержки</Typography>
            <Input value={codeInput} onChange={(e) => setCodeInput(e.target.value)}
                   placeholder="000000" slotProps={{ input: { maxLength: 6, inputMode: "numeric" } }}
                   sx={{ width: 160, letterSpacing: 3 }}
                   onKeyDown={(e) => e.key === "Enter" && onSearch()} />
          </Box>
          <Typography level="body-sm" color="neutral" sx={{ pb: 1 }}>или</Typography>
          <Box>
            <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>UUID книги</Typography>
            <Input value={bookInput} onChange={(e) => setBookInput(e.target.value)}
                   placeholder="123e4567-e89b-12d3-a456-426614174000"
                   sx={{ width: 340, fontFamily: "monospace", fontSize: 13 }}
                   onKeyDown={(e) => e.key === "Enter" && onSearch()} />
          </Box>
          <Typography level="body-sm" color="neutral" sx={{ pb: 1 }}>или</Typography>
          <Box>
            <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>Номер заказа</Typography>
            <Input value={orderInput} onChange={(e) => setOrderInput(e.target.value)}
                   placeholder="000123"
                   sx={{ width: 160 }}
                   onKeyDown={(e) => e.key === "Enter" && onSearch()} />
          </Box>
          <Button startDecorator={<SearchRoundedIcon />} onClick={onSearch} loading={loading}>Найти</Button>
        </Box>
        <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
          По коду — всё по клиенту (книги, заказы, процессы, логи). По UUID книги — логи именно этой книги.
          Приоритет поиска: UUID книги → номер заказа → код. События загружаются отдельно от ссылки.
        </Typography>
      </Sheet>

      {error && <Alert color="danger" variant="soft">{error}</Alert>}
      {data?.not_found && data?.mode === "order" && <Alert color="warning" variant="soft">Заказ <b>{data.order}</b> не найден.</Alert>}
      {data?.not_found && data?.mode !== "order" && <Alert color="warning" variant="soft">Клиент по коду <b>{data.code}</b> не найден.</Alert>}
      {data?.book_invalid && <Alert color="warning" variant="soft">«{data.book}» не похоже на UUID книги.</Alert>}

      {data?.found && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Карточка клиента / книги */}
            {data.support && (
              <Card variant="soft" color="primary">
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                  <Box>
                    <Typography level="body-xs" color="neutral">Код техподдержки</Typography>
                    <Typography level="title-lg">{data.support.code}</Typography>
                  </Box>
                  <Divider orientation="vertical" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" color="neutral">
                      session: {data.support.session_key || "—"} · uid: {data.support.client_uid || "—"}
                    </Typography>
                    <Typography level="body-xs" color="neutral">
                      IP: {data.support.ip || "—"} · создан {fmtDate(data.support.created_at)} · истекает {fmtDate(data.support.expires_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Tooltip title="Ссылка для входа под клиентом">
                        <IconButton
                          size="sm"
                          variant="soft"
                          onClick={() => copy(buildClientLink(data.support.user_link, linkCompress, linkConvert))}
                        >
                          <ContentCopyRoundedIcon />
                        </IconButton>
                      </Tooltip>
                      <Chip variant="outlined" color="neutral" sx={{ maxWidth: 260 }}>
                        <Typography level="body-xs" noWrap>
                          {buildClientLink(data.support.user_link, linkCompress, linkConvert)}
                        </Typography>
                      </Chip>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Checkbox size="sm" label="Сжатие фото" checked={linkCompress}
                                onChange={(e) => setLinkCompress(e.target.checked)} />
                      <Checkbox size="sm" label="Конвертер HEIC" checked={linkConvert}
                                onChange={(e) => setLinkConvert(e.target.checked)} />
                    </Box>
                  </Box>
                </Box>
              </Card>
            )}
            {data.order_info && (
              <Card variant="soft" color="primary">
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                  <Box>
                    <Typography level="body-xs" color="neutral">Заказ</Typography>
                    <Typography level="title-lg">№ {data.order_info.order_number}</Typography>
                  </Box>
                  <Divider orientation="vertical" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" color="neutral">
                      статус: {data.order_info.status} · книг: {data.order_info.books_count} · создан {fmtDate(data.order_info.created_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title="Скопировать ссылку">
                      <IconButton size="sm" variant="soft" onClick={() => copy(data.order_info.user_link)}>
                        <ContentCopyRoundedIcon />
                      </IconButton>
                    </Tooltip>
                    <Button size="sm" variant="solid" component="a"
                            href={data.order_info.user_link} target="_blank" rel="noreferrer">
                      Открыть книги заказа
                    </Button>
                  </Box>
                </Box>
              </Card>
            )}
            {data.book_info && (
              <Card variant="soft" color={data.book_info.exists ? "primary" : "warning"}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <Typography level="body-xs" color="neutral">Логи по книге · UUID</Typography>
                  <Typography level="body-sm" sx={{ fontFamily: "monospace" }}>{data.book_info.id}</Typography>
                  {data.book_info.exists ? (
                    <Button size="sm" variant="soft" endDecorator={<OpenInNewRoundedIcon />}
                            component="a" href={data.book_info.admin_url} target="_blank">
                      Открыть книгу
                    </Button>
                  ) : (
                    <Chip size="sm" color="warning" variant="soft">книга не найдена в базе (логи показаны)</Chip>
                  )}
                </Box>
              </Card>
            )}

            {data.inventory_note && <Typography level="body-xs" color="neutral">{data.inventory_note}</Typography>}

            {/* Счётчики */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <StatTile label="событий на странице" value={data.logs?.results?.length ?? "—"} color="primary" />
              <StatTile label="книг" value={counts.books} color="success" />
              <StatTile label="заказов" value={counts.orders} color="warning" />
              <StatTile label="процессов" value={counts.processes} color="neutral" />
            </Box>

            {/* Вкладки */}
            <Sheet variant="outlined" sx={{ borderRadius: "14px", overflow: "hidden" }}>
              <Tabs defaultValue={0} sx={{ bgcolor: "transparent" }}>
                <TabList sx={{ px: 1, pt: 1 }}>
                  <Tab>Логи</Tab>
                  <Tab>Книги ({counts.books ?? 0})</Tab>
                  <Tab>Заказы ({counts.orders ?? 0})</Tab>
                  <Tab>Процессы ({counts.processes ?? 0})</Tab>
                </TabList>

                {/* Логи */}
                <TabPanel value={0}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
                    <Select size="sm" placeholder="уровень" value={level || null}
                            onChange={(_e, v) => onFilter({ level: v || "" })} sx={{ minWidth: 130 }}>
                      <Option value="">— уровень —</Option>
                      {(data.level_choices || []).map((c) => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                    </Select>
                    <Select size="sm" placeholder="источник" value={source || null}
                            onChange={(_e, v) => onFilter({ source: v || "" })} sx={{ minWidth: 150 }}>
                      <Option value="">— источник —</Option>
                      {(data.source_choices || []).map((c) => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                    </Select>
                    <Select size="sm" value={range} onChange={(_e, value) => value && onFilter({ range: value })}>
                      <Option value="1h">Последний час</Option>
                      <Option value="24h">Последние сутки</Option>
                      <Option value="3d">Последние 3 дня</Option>
                      <Option value="10d">Последние 10 дней</Option>
                      <Option value="all">За всё время</Option>
                    </Select>
                    {data.mode === "order" && <Select size="sm" value={scope} onChange={(_e, value) => value && onFilter({ scope: value })}>
                      <Option value="target">Заказ и его книги</Option>
                      <Option value="client">Вся история клиента</Option>
                    </Select>}
                    <Button size="sm" variant="outlined" loading={logsLoading}
                            onClick={() => { setLogCursors([null]); loadLogs(activeQuery); }}>Обновить события</Button>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", ml: "auto" }}>
                      <Select size="sm" value={exportRange} onChange={(_e, value) => {
                        if (!value) return;
                        exportRequest.current?.abort(); setExporting(false);
                        setExportRange(value); setExportInfo(null);
                      }} disabled={exporting}>
                        <Option value="1h">за 1 час</Option>
                        <Option value="6h">за 6 часов</Option>
                        <Option value="24h">за 24 часа</Option>
                        <Option value="3d">за 3 суток</Option>
                        <Option value="10d">за 10 дней</Option>
                        <Option value="all">за всё время</Option>
                      </Select>
                      <Button size="sm" startDecorator={<DownloadRoundedIcon />} loading={exporting} onClick={() => onExport(false)}>Скачать JSON</Button>
                      {exportInfo?.next_before_id && <Button size="sm" variant="outlined" disabled={exporting} onClick={() => onExport(true)}>Следующая часть</Button>}
                    </Box>
                  </Box>
                  <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                    События показаны от новых к старым, по 25 на страницу. Подробности открываются по нажатию.
                  </Typography>
                  {logsLoading && <Typography level="body-sm" sx={{ mb: 1 }}>Загрузка событий…</Typography>}
                  {logsError && <Alert color="danger" sx={{ mb: 1 }}>{logsError}</Alert>}
                  {exportInfo && <Alert color={exportInfo.next_before_id || exportInfo.omitted ? "warning" : "success"} sx={{ mb: 1 }}>
                    Скачано событий: {exportInfo.count}.
                    {exportInfo.next_before_id ? " Есть ещё записи — нажмите «Следующая часть»." : " Все записи выбранного периода выгружены."}
                    {exportInfo.omitted ? ` Крупных payload не включено: ${exportInfo.omitted}; исходные записи сохранены в базе.` : ""}
                  </Alert>}
                  <Box sx={{ overflow: "auto" }}>
                    <Table hoverRow stickyHeader sx={{ "--TableCell-height": "auto" }}>
                      <thead>
                        <tr>
                          <th style={{ width: 160 }}>Время</th>
                          <th style={{ width: 80 }}>Уровень</th>
                          <th style={{ width: 110 }}>Источник</th>
                          <th style={{ width: 160 }}>Событие</th>
                          <th>Сообщение / URL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.logs?.results || []).map((l) => <LogRow key={l.id} log={l} params={activeQuery} />)}
                        {!logsLoading && !logsError && !data.logs?.results?.length && (
                          <tr><td colSpan={5}><Typography level="body-sm" color="neutral" sx={{ p: 1 }}>логов нет</Typography></td></tr>
                        )}
                      </tbody>
                    </Table>
                  </Box>
                  <Box sx={{ pointerEvents: logsLoading ? "none" : "auto", opacity: logsLoading ? 0.5 : 1 }}><Pager block={data.logs} onPage={(n) => onPage("logs_page", n)} /></Box>
                </TabPanel>

                {/* Книги */}
                <TabPanel value={1}>
                  <Box sx={{ overflow: "auto" }}>
                    <Table hoverRow>
                      <thead>
                        <tr><th>ID</th><th>Название</th><th>Размер</th><th>Стр.</th><th>Корзина</th><th>Создана</th><th></th></tr>
                      </thead>
                      <tbody>
                        {(data.books?.results || []).map((b) => (
                          <tr key={b.id}>
                            <td><Typography level="body-xs" sx={{ fontFamily: "monospace" }}>{short(b.id)}</Typography></td>
                            <td>{b.title}</td>
                            <td>{b.width_mm}×{b.height_mm}</td>
                            <td>{b.pages_count}</td>
                            <td>{b.in_cart ? "да" : "—"}</td>
                            <td><Typography level="body-xs">{fmtDate(b.created_at)}</Typography></td>
                            <td><IconButton size="sm" variant="plain" component="a" href={b.admin_url} target="_blank"><OpenInNewRoundedIcon /></IconButton></td>
                          </tr>
                        ))}
                        {!data.books?.results?.length && (
                          <tr><td colSpan={7}><Typography level="body-sm" color="neutral" sx={{ p: 1 }}>нет книг</Typography></td></tr>
                        )}
                      </tbody>
                    </Table>
                  </Box>
                  <Pager block={data.books} onPage={(n) => onPage("books_page", n)} />
                </TabPanel>

                {/* Заказы */}
                <TabPanel value={2}>
                  <Box sx={{ overflow: "auto" }}>
                    <Table hoverRow>
                      <thead>
                        <tr><th>№</th><th>Статус</th><th>Сумма</th><th>Клиент</th><th>Создан</th><th></th></tr>
                      </thead>
                      <tbody>
                        {(data.orders?.results || []).map((o) => (
                          <tr key={o.id}>
                            <td>{o.order_number || short(o.id)}</td>
                            <td><Chip size="sm" variant="soft">{o.status_display}</Chip></td>
                            <td>{(Number(o.total_price) || 0).toLocaleString("ru-RU")} ₽</td>
                            <td>
                              {o.client_name} {o.client_last_name}
                              <Typography level="body-xs" color="neutral">{o.client_phone} {o.client_email}</Typography>
                            </td>
                            <td><Typography level="body-xs">{fmtDate(o.created_at)}</Typography></td>
                            <td><IconButton size="sm" variant="plain" component="a" href={o.admin_url} target="_blank"><OpenInNewRoundedIcon /></IconButton></td>
                          </tr>
                        ))}
                        {!data.orders?.results?.length && (
                          <tr><td colSpan={6}><Typography level="body-sm" color="neutral" sx={{ p: 1 }}>нет заказов</Typography></td></tr>
                        )}
                      </tbody>
                    </Table>
                  </Box>
                  <Pager block={data.orders} onPage={(n) => onPage("orders_page", n)} />
                </TabPanel>

                {/* Процессы */}
                <TabPanel value={3}>
                  <Box sx={{ overflow: "auto" }}>
                    <Table hoverRow>
                      <thead>
                        <tr><th>Тип</th><th>Статус</th><th>Заказ</th><th>Создан</th><th>Завершён</th><th>Ошибка</th></tr>
                      </thead>
                      <tbody>
                        {(data.processes?.results || []).map((p) => (
                          <tr key={p.id}>
                            <td>{p.process_type_display}</td>
                            <td><Chip size="sm" variant="soft">{p.status_display}</Chip></td>
                            <td><Typography level="body-xs" sx={{ fontFamily: "monospace" }}>{short(p.order_id)}</Typography></td>
                            <td><Typography level="body-xs">{fmtDate(p.created_at)}</Typography></td>
                            <td><Typography level="body-xs">{p.finished_at ? fmtDate(p.finished_at) : "—"}</Typography></td>
                            <td><Typography level="body-xs" color="danger">{p.error_message || "—"}</Typography></td>
                          </tr>
                        ))}
                        {!data.processes?.results?.length && (
                          <tr><td colSpan={6}><Typography level="body-sm" color="neutral" sx={{ p: 1 }}>нет процессов</Typography></td></tr>
                        )}
                      </tbody>
                    </Table>
                  </Box>
                  <Pager block={data.processes} onPage={(n) => onPage("processes_page", n)} />
                </TabPanel>
              </Tabs>
            </Sheet>
          </Box>
        </motion.div>
      )}
    </Box>
  );
}